import React, { useState, useMemo } from 'react';
import Icon from '../../utils/icons';
import './WeatherMap.css';

const UI_STATE_KEY = 'weatherDashboard_uiState';

const WINDY_API_KEY = 'ybBIOa6NnJzfNmLsT6bdNLGpW1dQacyp';

// Windy overlay/layer options
const WINDY_LAYERS = [
  { id: 'radar', name: 'Radar', icon: 'radio', overlay: 'radar', product: 'radar' },
  { id: 'temp', name: 'Temp', icon: 'thermometer', overlay: 'temp', product: 'ecmwf' },
  { id: 'wind', name: 'Wind', icon: 'wind', overlay: 'wind', product: 'ecmwf' },
  { id: 'rain', name: 'Rain', icon: 'rain', overlay: 'rain', product: 'ecmwf' },
  { id: 'clouds', name: 'Clouds', icon: 'cloud', overlay: 'clouds', product: 'ecmwf' },
  { id: 'pressure', name: 'Pressure', icon: 'gauge', overlay: 'pressure', product: 'ecmwf' },
  { id: 'aqi', name: 'Air Quality', icon: 'leaf', overlay: 'pm2p5', product: 'cams' },
];

function WeatherMap({ coordinates, location }) {
  const [mapLayer, setMapLayer] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(UI_STATE_KEY) || '{}');
      return saved.mapLayer || 'radar';
    } catch { return 'radar'; }
  });

  const [zoom, setZoom] = useState(8);

  const changeMapLayer = (layer) => {
    setMapLayer(layer);
    try {
      const current = JSON.parse(localStorage.getItem(UI_STATE_KEY) || '{}');
      current.mapLayer = layer;
      localStorage.setItem(UI_STATE_KEY, JSON.stringify(current));
    } catch { /* ignore */ }
  };

  // Build the Windy embed URL
  const windyUrl = useMemo(() => {
    if (!coordinates?.lat || !coordinates?.lon) return null;

    const activeLayer = WINDY_LAYERS.find(l => l.id === mapLayer) || WINDY_LAYERS[0];

    const params = new URLSearchParams({
      type: 'map',
      location: 'coordinates',
      key: WINDY_API_KEY,
      metricRain: 'in',
      metricTemp: '°F',
      metricWind: 'mph',
      zoom: zoom.toString(),
      overlay: activeLayer.overlay,
      product: activeLayer.product,
      level: 'surface',
      lat: coordinates.lat.toFixed(4),
      lon: coordinates.lon.toFixed(4),
      message: 'true',
    });

    return `https://embed.windy.com/embed.html?${params.toString()}`;
  }, [coordinates, mapLayer, zoom]);

  const handleZoomIn = () => {
    if (zoom < 14) setZoom(zoom + 1);
  };

  const handleZoomOut = () => {
    if (zoom > 4) setZoom(zoom - 1);
  };

  if (!coordinates?.lat || !coordinates?.lon) {
    return (
      <div className="weather-map">
        <div className="card-header">
          <h2 className="card-title">
            <Icon name="map" size={18} /> Weather Map
          </h2>
        </div>
        <div className="map-placeholder">
          <Icon name="globe" size={48} className="placeholder-icon" />
          <p>Select a location to view the map</p>
        </div>
      </div>
    );
  }

  return (
    <div className="weather-map">
      <div className="card-header">
        <div className="title-section">
          <h2 className="card-title">
            <Icon name="map" size={18} /> Weather Map
          </h2>
          <span className="card-subtitle">
            <Icon name="mapPin" size={12} /> {location}
          </span>
        </div>

        <div className="map-controls">
          <div className="zoom-controls">
            <button className="zoom-btn" onClick={handleZoomIn} disabled={zoom >= 14} title="Zoom in">
              <Icon name="zoomIn" size={14} />
            </button>
            <span className="zoom-level">{zoom}</span>
            <button className="zoom-btn" onClick={handleZoomOut} disabled={zoom <= 4} title="Zoom out">
              <Icon name="zoomOut" size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Layer selector */}
      <div className="layer-selector">
        {WINDY_LAYERS.map((layer) => (
          <button
            key={layer.id}
            className={`layer-btn ${mapLayer === layer.id ? 'active' : ''}`}
            onClick={() => changeMapLayer(layer.id)}
            title={layer.name}
          >
            <Icon name={layer.icon} size={16} />
            <span className="layer-name">{layer.name}</span>
          </button>
        ))}
      </div>

      {/* Windy embed */}
      <div className="map-container">
        <iframe
          title="Windy Weather Map"
          src={windyUrl}
          className="windy-iframe"
          frameBorder="0"
          allowFullScreen
        />
      </div>

      {/* Footer */}
      <div className="map-footer">
        <div className="map-coords">
          <span>{coordinates.lat.toFixed(4)}°N, {Math.abs(coordinates.lon).toFixed(4)}°{coordinates.lon >= 0 ? 'E' : 'W'}</span>
        </div>
        <div className="map-attribution">
          Powered by <a href="https://www.windy.com" target="_blank" rel="noopener noreferrer">Windy.com</a>
        </div>
      </div>
    </div>
  );
}

export default WeatherMap;
